import { useMemo, createElement } from 'react';

import { TitleLevel } from 'types';
import { Flex } from 'components/flex';
import { formatPrice } from 'utils/helpers';

import * as Styles from './styles';
import { SubGroup } from './subrows';
import { CardProps, AdjustmentCardProps } from './types';

export const Card = ({
  name,
  isRed,
  value,
  showPositiveSign,
  titleLevel = TitleLevel.P,
}: CardProps) => {
  const formattedValue =
    showPositiveSign && value > 0
      ? `+${formatPrice(value)}`
      : formatPrice(isRed ? `-${Math.abs(value)}` : value);

  return (
    <Flex gap={16} justifyContent="space-between">
      {createElement(titleLevel, { children: name })}
      <Styles.Text isRed={isRed} isBold={titleLevel !== TitleLevel.P}>
        {formattedValue}
      </Styles.Text>
    </Flex>
  );
};

export const AdjustmentCard = ({
  name,
  value,
  adjustments,
  currentEventName,
}: AdjustmentCardProps) => {
  const currentEventAmount = useMemo(() => {
    if (!adjustments?.length) {
      return 0;
    }

    const otherEventsTotal = adjustments.reduce(
      (sum, adj) => sum + Math.abs(adj.amount),
      0
    );

    const currentEventAmount = parseFloat(
      (Math.abs(value) - otherEventsTotal).toFixed(2)
    );

    return currentEventAmount;
  }, [adjustments, value]);

  if (!adjustments?.length) {
    return <Card isRed name={name} value={value} />;
  }

  return (
    <SubGroup>
      <Card isRed name={name} value={value} />
      <Styles.SubRows>
        {adjustments.map((adj, index) => (
          <Styles.SubRow key={index}>
            <span>{adj.eventName}</span>
            <Styles.SubAmount $negative>−{formatPrice(Math.abs(adj.amount))}</Styles.SubAmount>
          </Styles.SubRow>
        ))}
        {currentEventAmount > 0 && currentEventName && (
          <Styles.SubRow>
            <span>{currentEventName}</span>
            <Styles.SubAmount $negative>−{formatPrice(currentEventAmount)}</Styles.SubAmount>
          </Styles.SubRow>
        )}
      </Styles.SubRows>
    </SubGroup>
  );
};
